import { useMutation, useQueryClient } from '@tanstack/react-query';

import { packagingApi } from 'api/packaging';

import { packagingPurchaseOrderQueryKeys } from '../queries/PackagingPOQueries';

/**
 * Bulk table actions for packaging PO V2 list: send to vendor, PDF download.
 * Network orchestration lives here; callers keep selection UX and pre-mutation validation.
 *
 * Same canonical shape as useBulkPurchaseOrderTableMutations (Phase A/B/C in onSuccess).
 *
 * @param {Object} options
 * @param {(payload: { text: string, level: 'success'|'error'|'warning'|'info' }) => void} [options.onNotify]
 */
export function useBulkPackagingPOTableMutations({ onNotify } = {}) {
    const queryClient = useQueryClient();

    const invalidatePackagingPurchaseOrdersList = () =>
        queryClient.invalidateQueries({ queryKey: packagingPurchaseOrderQueryKeys.all });

    const bulkSendToVendorMutation = useMutation({
        mutationFn: async ({ purchaseOrderIds }) => {
            const settled = await Promise.allSettled(
                purchaseOrderIds.map((id) => packagingApi.sendPurchaseOrderToVendor(id)),
            );
            const res = settled.map((s) => {
                if (s.status === 'fulfilled') {
                    return s.value;
                }
                const { reason } = s;
                const msg = reason instanceof Error ? reason.message : String(reason ?? 'Request failed.');
                return { status: false, msg };
            });
            const succeededPurchaseOrderIds = purchaseOrderIds.filter((_, i) => res[i]?.status);
            const allSucceeded = res.every((item) => item?.status);
            const firstFailed = res.find((r) => !r?.status);
            return {
                allSucceeded,
                firstFailedMsg: firstFailed?.msg,
                succeededPurchaseOrderIds,
                totalAttempted: purchaseOrderIds.length,
            };
        },
        onSuccess: async (data) => {
            // Phase A: refresh packaging PO list
            if (data.succeededPurchaseOrderIds.length > 0) {
                await invalidatePackagingPurchaseOrdersList();
            }
            // Phase B: guard toast channel
            if (!onNotify) return;
            // Phase C: exactly one summary toast
            if (data.allSucceeded) {
                onNotify({ level: 'success', text: 'Sent packaging PO(s) to vendor(s)' });
            } else if (data.succeededPurchaseOrderIds.length > 0) {
                const sent = data.succeededPurchaseOrderIds.length;
                const { totalAttempted } = data;
                const detail = data.firstFailedMsg ? ` ${data.firstFailedMsg}` : '';
                onNotify({
                    level: 'warning',
                    text: `Sent ${sent} of ${totalAttempted} packaging PO(s) to vendors; some could not be sent.${detail}`,
                });
            } else {
                const msg = data.firstFailedMsg
                    ? `Issue sending the selected packaging POs to vendors. ${data.firstFailedMsg}`
                    : 'Issue sending the selected packaging POs to vendors.';
                onNotify({ level: 'error', text: msg });
            }
        },
        onError: (e) => {
            if (!onNotify) return;
            const msg = e?.message
                ? `Failed to send packaging purchase orders to vendors. ${e.message}`
                : 'Failed to send packaging purchase orders to vendors.';
            onNotify({ level: 'error', text: msg });
        },
    });

    const bulkDownloadPdfsMutation = useMutation({
        mutationFn: async ({ pdfItems }) => {
            const settled = await Promise.allSettled(
                pdfItems.map(async ({ id, poRow }) => {
                    if (!poRow) {
                        return {
                            id,
                            success: false,
                            errorMsg: `Could not find data for PO ID ${id} to download PDF.`,
                        };
                    }
                    const result = await packagingApi.downloadPackagingPOPDF(poRow.id, poRow.vendorName);
                    if (result?.status) {
                        return { id: poRow.id, success: true };
                    }
                    const errMsg = result?.msg
                        ? `Error downloading PDF for PO ${poRow.id}. ${result.msg}`
                        : `Error downloading PDF for PO ${poRow.id}.`;
                    return { id: poRow.id, success: false, errorMsg: errMsg };
                }),
            );
            const results = settled.map((s, i) => {
                if (s.status === 'fulfilled') {
                    return s.value;
                }
                const { id } = pdfItems[i];
                const { reason } = s;
                const errText = reason instanceof Error ? reason.message : String(reason ?? 'Request failed.');
                return {
                    id,
                    success: false,
                    errorMsg: `Error downloading PDF for PO ${id}. ${errText}`,
                };
            });
            return { results };
        },
        onSuccess: async (data) => {
            const { results } = data;
            let successCount = 0;
            let errorCount = 0;
            results.forEach((r) => {
                if (r.success) successCount += 1;
                else errorCount += 1;
            });
            // Phase A: refresh packaging PO list
            if (successCount > 0) {
                await invalidatePackagingPurchaseOrdersList();
            }
            // Phase B: guard toast channel
            if (!onNotify) return;
            // Phase C: exactly one summary toast
            const firstFailedResult = results.find((r) => !r.success);
            const firstFailureDetail = firstFailedResult?.errorMsg ? ` ${firstFailedResult.errorMsg}` : '';
            if (successCount > 0 && errorCount === 0) {
                onNotify({
                    level: 'success',
                    text: `Successfully initiated download for ${successCount} PDF(s).`,
                });
            } else if (successCount > 0 && errorCount > 0) {
                onNotify({
                    level: 'warning',
                    text: `Initiated download for ${successCount} PDF(s). Failed to initiate for ${errorCount} PDF(s).${firstFailureDetail}`,
                });
            } else if (errorCount > 0 && successCount === 0) {
                onNotify({
                    level: 'error',
                    text: `Failed to initiate download for ${errorCount} PDF(s).${firstFailureDetail}`,
                });
            }
        },
        onError: (e) => {
            if (!onNotify) return;
            const msg = e?.message ? `Failed to download PDFs. ${e.message}` : 'Failed to download PDFs.';
            onNotify({ level: 'error', text: msg });
        },
    });

    return {
        bulkSendToVendorMutation,
        bulkDownloadPdfsMutation,
    };
}

useBulkPackagingPOTableMutations.displayName = 'useBulkPackagingPOTableMutations';
